import * as React from "react";
import { ClipboardIcon, Ellipsis, Group, LinkIcon, MessageSquareIcon, Network } from "lucide-react";
import { useToggle } from "react-use";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip } from "@/components/ui/tooltip";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useFeatureAccess } from "@/hooks/use-feature-access";
import { DocumentGroupCreateModal } from "@/modules/documentGroup/createModal";
import { type FolderItem } from "@/types/fse";
import { AddFilesModalContent } from "@/views/drive/modals/addFilesModal/content";
import { ContractSummaryRegenerateDialog } from "@/views/drive/modals/contractSummaryRegenerateDialog";
import CreateFolderModal from "@/views/drive/modals/createFolderModal";
import { UploadModal } from "@/views/drive/modals/uploadModal";
import { toUploadDestination } from "@/views/drive/upload/uploadDestination";
import type { SupplierContractFolderContext } from "@/views/drive/useSupplierContractFolder";
import {
  CONTRACT_SUMMARY_ACTION_LABELS,
  contractSummaryAction,
} from "@/views/suppliers/components/contract-summary-status";

interface DriveHeaderActionsProps {
  parentFolder: FolderItem | null;
  currentFolder: FolderItem;
  isRootFolder: boolean;
  askAIButton?: boolean;
  addFilesButton?: boolean;
  hideOverflow?: boolean;
  /** Present on a Supplier contract folder: enables the one-click contract summary action. */
  contractFolder?: SupplierContractFolderContext | null;
  canCreateContractSummary?: boolean;
}

export const DriveHeaderActions: React.FC<DriveHeaderActionsProps> = ({
  parentFolder,
  currentFolder,
  isRootFolder,
  askAIButton,
  addFilesButton,
  hideOverflow = false,
  contractFolder = null,
  canCreateContractSummary = false,
}) => {
  const navigate = useNavigate();
  const [isContractSummaryOpen, setIsContractSummaryOpen] = React.useState(false);
  const authenticatedUser = useAuthenticatedUser();
  const { hasAccess } = useFeatureAccess();
  const [isCreateGroupOpen, toggleCreateGroupOpen] = useToggle(false);
  const [isAddFilesOpen, toggleAddFilesOpen] = useToggle(false);
  const destination = toUploadDestination(currentFolder, authenticatedUser.id, isRootFolder);
  const isReadOnly = !destination.canUpload;
  const canUseSnapshot = !!parentFolder && hasAccess("reports");
  const canUseQa = !!parentFolder && !!askAIButton;
  const showContractSummary = Boolean(contractFolder) && canCreateContractSummary;
  // "Create" until a summary was published for the family, "Update" from then on.
  const summaryAction = contractSummaryAction(contractFolder?.folder);

  return (
    <>
      <div className="flex flex-wrap justify-end gap-2 sm:flex-nowrap">
        {canUseSnapshot && (
          <Button
            variant="second-white"
            type="button"
            className="hidden md:inline-flex"
            onClick={() => navigate(`/reports/${currentFolder.id}`)}
            data-testid="drive-header-snapshot"
          >
            <ClipboardIcon className="mr-2 h-5 w-5" />
            Snapshot
          </Button>
        )}
        {canUseQa && (
          <Button
            variant="second-white"
            type="button"
            className="hidden md:inline-flex"
            onClick={() => navigate(`/chat/${currentFolder.id}`)}
          >
            <MessageSquareIcon className="mr-2 h-5 w-5" />
            Q&amp;A
          </Button>
        )}
        <CreateFolderModal currentFolderId={currentFolder.id} isReadOnly={isReadOnly} />
        {showContractSummary && (
          <Button variant="second-white" type="button" onClick={() => setIsContractSummaryOpen(true)}>
            <Network className="mr-2 h-5 w-5" />
            {CONTRACT_SUMMARY_ACTION_LABELS[summaryAction]}
          </Button>
        )}
        {/* Upload stays the right-most primary action. */}
        <UploadModal destination={destination} isReadOnly={isReadOnly} />
        {!hideOverflow && (
          <DropdownMenu>
            <Tooltip text="More folder actions" className="rounded bg-black text-white">
              <DropdownMenuTrigger asChild>
                <Button variant="second-white" size="icon" type="button" aria-label="More folder actions">
                  <Ellipsis className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent align="end">
              {canUseSnapshot && (
                <DropdownMenuItem className="md:hidden" onSelect={() => navigate(`/reports/${currentFolder.id}`)}>
                  <ClipboardIcon className="mr-2 h-4 w-4" />
                  Snapshot
                </DropdownMenuItem>
              )}
              {canUseQa && (
                <DropdownMenuItem className="md:hidden" onSelect={() => navigate(`/chat/${currentFolder.id}`)}>
                  <MessageSquareIcon className="mr-2 h-4 w-4" />
                  Q&amp;A
                </DropdownMenuItem>
              )}
              {(canUseSnapshot || canUseQa) && <DropdownMenuSeparator className="md:hidden" />}
              <DropdownMenuItem onSelect={() => toggleCreateGroupOpen(true)}>
                <Group className="mr-2 h-4 w-4" />
                Create Group
              </DropdownMenuItem>
              {parentFolder && addFilesButton && (
                <DropdownMenuItem disabled={isReadOnly} onSelect={() => toggleAddFilesOpen(true)}>
                  <LinkIcon className="mr-2 h-4 w-4" />
                  Add Files
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {isCreateGroupOpen && (
        <DocumentGroupCreateModal
          parentFolderId={currentFolder.id}
          isOpen={isCreateGroupOpen}
          setIsOpen={toggleCreateGroupOpen}
        />
      )}
      <Dialog open={isAddFilesOpen} onOpenChange={toggleAddFilesOpen}>
        {isAddFilesOpen && <AddFilesModalContent currentFolderId={currentFolder.id} setIsOpen={toggleAddFilesOpen} />}
      </Dialog>
      {showContractSummary && contractFolder && (
        <ContractSummaryRegenerateDialog
          open={isContractSummaryOpen}
          onOpenChange={setIsContractSummaryOpen}
          supplierContractFolderId={contractFolder.relationId}
          action={summaryAction}
        />
      )}
    </>
  );
};
